import React, { useRef, useEffect, useState, useImperativeHandle } from 'react';

import { $document, $window } from '@umu/umu-dom';
import { AutosizeController } from './utils/controller';

import './autoSizeEditor.scss';

export interface AutoSizeEditorRef {
    textAreaElement: HTMLTextAreaElement | null;
    controller: AutosizeController | undefined;
}
export interface IProps {
    placeholder: string;
    container: string | HTMLElement;
    value: string;
    maxLength?: number;
    minHeight?: string;
    autoFocus?: boolean;
    onFocus?: () => void;
    onChange: (value: string, composition: string) => void;
}

export const AutoSizeEditor = React.forwardRef<AutoSizeEditorRef, IProps>((props, ref) => {
    const { placeholder, container, value, onChange, minHeight, maxLength = Infinity, autoFocus, onFocus } = props;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const isCompositionRef = useRef('');
    const preRef = useRef<HTMLPreElement>(null);
    const controllerRef = useRef<AutosizeController>();
    const [content, setContent] = useState(value);
    const [height, setHeight] = useState(minHeight);

    useEffect(adjustMiniHeight, [minHeight, container, value]);

    function adjustMiniHeight() {
        $window.setTimeout(() => {
            let h = minHeight;
            const mirrorEl = preRef.current;
            if (mirrorEl) {
                h = mirrorEl.clientHeight > parseInt(minHeight || '', 10) ? 'none' : minHeight;
            }
            setHeight(h);
        }, 200);
    }

    const updateContent = () => {
        if (!textareaRef || !textareaRef.current) {
            return;
        }
        let text = textareaRef.current.value;
        if (isCompositionRef.current !== 'compositionstart' && text.length > maxLength) {
            text = text.slice(0, maxLength);
        }

        onChange(text, isCompositionRef.current);
        setContent(text);
    };

    useEffect(() => {
        if (!textareaRef.current) {
            return;
        }

        let containerEl = null;
        if (typeof container === 'string') {
            containerEl = $document.querySelector(container) as HTMLElement;
        }
        controllerRef.current = new AutosizeController(textareaRef.current, containerEl);
        controllerRef.current.init();
        if (autoFocus) {
            textareaRef.current?.setSelectionRange(-1, -1);
        }
        return () => {
            if (controllerRef.current) {
                controllerRef.current.destory();
            }
        };
    }, []);

    const onInputFocus = (_e: React.FocusEvent<HTMLTextAreaElement>) => {
        if (onFocus) {
            onFocus();
        }
    };

    const onComposition = (e: React.CompositionEvent<HTMLTextAreaElement>) => {
        isCompositionRef.current = e.type;
        updateContent();
    };

    useImperativeHandle(ref, () => ({ textAreaElement: textareaRef.current, controller: controllerRef.current }), [
        controllerRef.current,
    ]);

    return (
        <div className="auto-height-textarea">
            <textarea
                autoComplete="off"
                placeholder={placeholder}
                className="textarea"
                ref={textareaRef}
                value={value}
                onChange={updateContent}
                onFocus={onInputFocus}
                onCompositionStart={onComposition}
                onCompositionEnd={onComposition}
                style={{
                    minHeight,
                    height,
                }}
            ></textarea>
            <pre ref={preRef} className="textarea-mirror textarea">
                {content}
            </pre>
        </div>
    );
});
